// --- FEAT-053 TASK-044 (erro de turno) ---
/**
 * Maps the SSE `error` event of a turn that ended BEFORE the assistant reply started (no `message_started`)
 * to the info the SendErrorBanner renders in its `turn` variant. Pure; shared by the chat page and the /code
 * page (FEAT-053 TASK-045).
 *
 * Rules, line by line:
 * - E_QUOTA_EXCEEDED / E_BUDGET_EXCEEDED -> null. The server emits the dedicated `quota_exceeded` /
 *   `budget_exceeded` event right before the `error` (chat-stream.controller.ts :108 -> :110 for budget,
 *   :374 -> :384 for quota), and that event already raises its own warning (ChatInputQuotaBar). A second banner
 *   would duplicate it.
 * - E_MODEL_NO_ACCESS -> 403 (mirrors the HTTP refusal of the same guard). The event carries no `details.reason`,
 *   so the "provider only lists models" case reads as the generic no-access text here (follow-up F-044-1).
 * - E_MODEL_NOT_FOUND -> 404 (the model vanished; choosing another one is the way out).
 * - E_AGENT_BUSY -> 409.
 * - Anything else, including an empty or unknown error_code -> 500 with the code kept only for dispatch.
 *
 * `status` is synthetic (never shown). `data.message` is NEVER copied: the server texts are not localized
 * (some are English) and the screen renders i18n text only.
 */

import type { SendErrorInfo } from '@/components/chat/SendErrorBanner';
import type { StreamErrorEventData } from '@/types/stream.types';

const STATUS_BY_CODE: Readonly<Record<string, number>> = {
  E_MODEL_NO_ACCESS: 403,
  E_MODEL_NOT_FOUND: 404,
  E_AGENT_BUSY: 409,
};

const OWN_WARNING_CODES: ReadonlySet<string> = new Set(['E_QUOTA_EXCEEDED', 'E_BUDGET_EXCEEDED']);

export function turnErrorInfoFromStreamError(data: StreamErrorEventData): SendErrorInfo | null {
  const code = typeof data.error_code === 'string' && data.error_code !== '' ? data.error_code : undefined;
  if (code !== undefined && OWN_WARNING_CODES.has(code)) return null;
  const status = (code !== undefined ? STATUS_BY_CODE[code] : undefined) ?? 500;
  return { kind: 'turn', status, code };
}
// --- fim FEAT-053 TASK-044 (erro de turno) ---
